// Costruzione dei payload Sentry-compatibili inviati all'ingest CloseYourIt.
// Specchio di `CloseYourIt::Events::{ErrorEvent,MessageEvent,LogEvent}` (gem) e
// dei rispettivi in `closeyourit-dart`. Tutti i campi sono snake_case.

import { runtimeContext } from './runtime'
import { parseStack } from './stackTrace'
import { scrub, scrubMessage } from './scrubber'
import type { EventLevel, EventPayload, LogLevel } from './types'
import { uuidV4NoDashes } from './uuid'
import { PLATFORM, SDK_NAME, SDK_VERSION } from './version'

const SDK_INFO = { name: SDK_NAME, version: SDK_VERSION } as const

/// Rappresentazione normalizzata di un errore, indipendente dal tipo in ingresso.
export interface NormalizedError {
  type: string
  value: string
  stack: string | undefined
}

/// Riduce un input arbitrario (`Error`, stringa, oggetto, valore) a `{type,value,stack}`.
export function normalizeError(input: unknown): NormalizedError {
  if (input instanceof Error) {
    return {
      type: input.name || 'Error',
      value: input.message || String(input),
      stack: input.stack,
    }
  }
  if (typeof input === 'string') {
    return { type: 'Error', value: input, stack: undefined }
  }
  if (input && typeof input === 'object') {
    const obj = input as { name?: unknown; message?: unknown; stack?: unknown }
    const type = typeof obj.name === 'string' ? obj.name : 'Error'
    const value = typeof obj.message === 'string' ? obj.message : safeStringify(input)
    const stack = typeof obj.stack === 'string' ? obj.stack : undefined
    return { type, value, stack }
  }
  return { type: 'Error', value: String(input), stack: undefined }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    return String(value)
  }
}

interface ErrorEventInput {
  error: NormalizedError
  environment: string
  release?: string | undefined
  handled: boolean
  level: EventLevel
  scopeHash: EventPayload
  traceId?: string | undefined
  /// Id della sessione di replay in corso → `contexts.replay.replay_id`, lega
  /// l'errore al video della sessione.
  replayId?: string | undefined
  contexts?: Record<string, unknown> | undefined
  /// `contexts.runtime` (default: rilevato dall'ambiente).
  runtime?: Record<string, unknown> | undefined
  /// Request context automatico; il `request` dello scope ha precedenza.
  request?: Record<string, unknown> | undefined
  /// Hostname del server (Node) → campo top-level `server_name`, in parità con la
  /// gem Ruby. Risolto dal client (via `serverName()`) e presente solo lato server
  /// con `sendServerName` attivo; nel browser resta assente.
  serverName?: string | undefined
}

/// Payload errore per `POST …/events`.
export function buildErrorEvent(input: ErrorEventInput): EventPayload {
  const { base, scopeContexts } = splitScope(input.scopeHash)
  const frames = parseStack(input.error.stack)

  const payload: EventPayload = {
    event_id: uuidV4NoDashes(),
    timestamp: nowIso(),
    platform: PLATFORM,
    level: input.level,
    environment: input.environment,
    ...(input.release ? { release: input.release } : {}),
    ...(input.serverName ? { server_name: input.serverName } : {}),
    exception: {
      values: [
        {
          type: input.error.type,
          value: scrubMessage(input.error.value),
          stacktrace: { frames },
          mechanism: { type: PLATFORM, handled: input.handled },
        },
      ],
    },
    contexts: {
      runtime: input.runtime ?? runtimeContext(),
      ...scopeContexts,
      ...scrub(input.contexts ?? {}),
      ...(input.replayId ? { replay: { replay_id: input.replayId } } : {}),
    },
    sdk: SDK_INFO,
    // Il request automatico precede lo spread dello scope: un
    // `scope.setRequest` manuale lo sovrascrive.
    ...(input.request ? { request: input.request } : {}),
    ...base,
    ...(input.traceId ? { trace_id: input.traceId } : {}),
  }
  return payload
}

interface MessageEventInput {
  message: string
  environment: string
  release?: string | undefined
  level: EventLevel
  scopeHash: EventPayload
  traceId?: string | undefined
  replayId?: string | undefined
  runtime?: Record<string, unknown> | undefined
  request?: Record<string, unknown> | undefined
  /// Hostname del server (Node) → campo top-level `server_name` (vedi
  /// `ErrorEventInput`). Assente nel browser.
  serverName?: string | undefined
}

/// Payload messaggio diagnostico per `POST …/events`.
export function buildMessageEvent(input: MessageEventInput): EventPayload {
  const { base, scopeContexts } = splitScope(input.scopeHash)
  return {
    event_id: uuidV4NoDashes(),
    timestamp: nowIso(),
    platform: PLATFORM,
    level: input.level,
    environment: input.environment,
    ...(input.release ? { release: input.release } : {}),
    ...(input.serverName ? { server_name: input.serverName } : {}),
    message: { formatted: scrubMessage(input.message) },
    contexts: {
      runtime: input.runtime ?? runtimeContext(),
      ...scopeContexts,
      ...(input.replayId ? { replay: { replay_id: input.replayId } } : {}),
    },
    sdk: SDK_INFO,
    ...(input.request ? { request: input.request } : {}),
    ...base,
    ...(input.traceId ? { trace_id: input.traceId } : {}),
  }
}

interface LogEventInput {
  message: string
  level: LogLevel
  environment: string
  release?: string | undefined
  logger?: string | undefined
  traceId?: string | undefined
  attributes?: Record<string, unknown> | undefined
}

/// Voce di log per `POST …/logs` (inviata in batch, come array).
export function buildLogEvent(input: LogEventInput): EventPayload {
  const attributes =
    input.attributes && Object.keys(input.attributes).length > 0
      ? scrub(input.attributes)
      : undefined

  return {
    event_id: uuidV4NoDashes(),
    timestamp: nowIso(),
    level: input.level,
    // Scrub del message anche sui log (non solo su errori/message-event): un log può contenere
    // PII/segreti quanto un errore (es. `Failed auth email=… token=…`). Parità con buildErrorEvent.
    message: scrubMessage(input.message),
    ...(attributes ? { attributes } : {}),
    ...(input.logger ? { logger: input.logger } : {}),
    ...(input.traceId ? { trace_id: input.traceId } : {}),
    environment: input.environment,
    ...(input.release ? { release: input.release } : {}),
    sdk: SDK_INFO,
  }
}

// Estrae `contexts` dallo scope hash: va fuso nei contexts dell'evento, non
// spread al top-level (parità con dart/ruby).
function splitScope(scopeHash: EventPayload): {
  base: EventPayload
  scopeContexts: Record<string, unknown>
} {
  const base: EventPayload = { ...scopeHash }
  const scopeContexts = (base['contexts'] as Record<string, unknown>) ?? {}
  delete base['contexts']
  return { base, scopeContexts }
}

function nowIso(): string {
  return new Date().toISOString()
}
